import React, { useState } from 'react';
import { Button, ButtonGroup, Col, Row } from 'react-bootstrap';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ComposedChart, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';
import { getHealthRange } from '../api/trainer';
import ChartCard from '../components/ChartCard';
import PageHeader from '../components/PageHeader';
import StatusMessage from '../components/StatusMessage';
import { useAsync } from '../hooks/useAsync';
import StatTile from '../components/StatTile';
import { formatNumber, isoDaysAgo, shortDate } from '../utils/format';
import { MetricKey, historyPath } from '../utils/healthMetrics';
import { Y_AXIS, yAxisProps } from '../utils/axis';
import { BodyBatteryDay, DailyHealth, HrvDay, SleepNight, WeightDay } from '../api/types';

const RANGES = [7, 30, 90];

const sum = (...values: Array<number | null>) => values.reduce<number>((total, v) => total + (v ?? 0), 0);
const toHours = (seconds: number | null) => (seconds == null ? null : Number((seconds / 3600).toFixed(2)));
const pct = (share: number | null) => (share == null ? null : Math.round(share * 100));
/** The newest row of an oldest-first list that has any data, or null. */
const lastOf = <T,>(list: T[]): T | null => (list.length > 0 ? list[list.length - 1] : null);
const AXIS = { stroke: 'var(--text-muted)', fontSize: 12 };
const MARGIN = { top: 8, right: 8, bottom: 0, left: -12 };

interface Tile {
  label: string;
  to: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
}

interface Latest {
  daily: DailyHealth | null;
  sleep: SleepNight | null;
  hrv: HrvDay | null;
  battery: BodyBatteryDay | null;
  weight: WeightDay | null;
}

const dash = '—';

/** One tile per headline number of the newest day in the range. A tile for data that is missing shows a dash. */
function latestTiles(latest: Latest, historyTo: (key: MetricKey) => string): Tile[] {
  const { daily, sleep, hrv, battery, weight } = latest;
  const hours = sleep?.totalSleepS == null ? dash : `${formatNumber(sleep.totalSleepS / 3600, 1)} h`;
  return [
    { label: 'Steps', to: historyTo('steps'), value: formatNumber(daily?.steps), hint: daily?.stepGoal ? `goal ${formatNumber(daily.stepGoal)}` : undefined },
    { label: 'Resting HR', to: historyTo('resting-hr'), value: daily?.restingHr == null ? dash : `${daily.restingHr} bpm`, hint: daily?.hrMax ? `max ${daily.hrMax} bpm` : undefined },
    { label: 'Sleep', to: historyTo('sleep'), value: hours, hint: sleep?.sleepScore == null ? undefined : `score ${sleep.sleepScore}` },
    { label: 'HRV last night', to: historyTo('hrv'), value: hrv?.lastNightAvg == null ? dash : `${hrv.lastNightAvg} ms`, hint: hrv?.status ?? undefined },
    { label: 'Body battery', to: historyTo('body-battery'), value: battery?.highest == null ? dash : `${battery.highest}`, hint: battery?.lowest == null ? undefined : `low ${battery.lowest}` },
    { label: 'Stress', to: historyTo('stress'), value: daily?.stressAvg == null ? dash : `${daily.stressAvg}`, hint: daily?.stressQualifier ?? undefined },
    { label: 'Weight', to: historyTo('weight'), value: weight?.weightKg == null ? dash : `${formatNumber(weight.weightKg, 1)} kg`, hint: weight?.bodyFatPct == null ? undefined : `${formatNumber(weight.bodyFatPct, 1)}% fat` },
    { label: 'Blood oxygen', to: historyTo('spo2'), value: daily?.spo2Avg == null ? dash : `${daily.spo2Avg}%`, hint: daily?.spo2Min == null ? undefined : `low ${daily.spo2Min}%` },
  ];
}

const HealthPage: React.FC = () => {
  const [days, setDays] = useState(30);

  const data = useAsync(async () => {
    const range = { from: isoDaysAgo(days), to: isoDaysAgo(0) };
    const [daily, sleep, hrv, battery, weight] = await Promise.all([
      getHealthRange('daily', range),
      getHealthRange('sleep', range),
      getHealthRange('hrv', range),
      getHealthRange('body-battery', range),
      getHealthRange('weight', range),
    ]);
    return {
      latest: {
        daily: lastOf(daily),
        sleep: lastOf(sleep),
        hrv: lastOf(hrv),
        battery: lastOf(battery),
        weight: lastOf(weight),
      },
      sleepRows: sleep.map(n => ({
        date: shortDate(n.date),
        hours: toHours(n.totalSleepS),
        score: n.sleepScore,
        deep: toHours(n.deepSleepS),
        rem: toHours(n.remSleepS),
        light: toHours(n.lightSleepS),
        awake: toHours(n.awakeS),
      })),
      hrvRows: hrv.map(d => ({
        date: shortDate(d.date),
        lastNight: d.lastNightAvg,
        weekly: d.weeklyAvg,
        balancedLow: d.baselineBalancedLow,
        balancedUpper: d.baselineBalancedUpper,
      })),
      stressShareRows: daily.map(d => ({
        date: shortDate(d.date),
        rest: pct(d.stressRestShare),
        low: pct(d.stressLowShare),
        medium: pct(d.stressMediumShare),
        high: pct(d.stressHighShare),
      })),
      weightRows: weight.map(d => ({ date: shortDate(d.date), kg: d.weightKg })),
      respirationRows: daily.map(d => ({ date: shortDate(d.date), avg: d.respirationAvg, min: d.respirationMin, max: d.respirationMax })),
      spo2Rows: daily.map(d => ({ date: shortDate(d.date), avg: d.spo2Avg, min: d.spo2Min })),
      caloriesRows: daily.map(d => ({ date: shortDate(d.date), active: d.caloriesActive, resting: d.caloriesResting })),
      floorsRows: daily.map(d => ({ date: shortDate(d.date), floors: d.floorsAscended })),
      intensityRows: daily.map(d => ({ date: shortDate(d.date), moderate: d.moderateMinutes, vigorous: d.vigorousMinutes })),
      hydrationRows: daily.map(d => ({ date: shortDate(d.date), ml: d.hydrationMl, goal: d.hydrationGoalMl })),
      restingRows: daily.map(d => ({ date: shortDate(d.date), resting: d.restingHr })),
      stepRows: daily.map(d => ({ date: shortDate(d.date), steps: d.steps })),
      stressRows: daily.map(d => ({ date: shortDate(d.date), stress: d.stressAvg })),
      batteryRows: battery.map(d => ({ date: shortDate(d.date), high: d.highest, low: d.lowest })),
    };
  }, [days]);

  const rows = data.data;
  const empty = (list: unknown[] | undefined) => !data.loading && (list?.length ?? 0) === 0;
  // History pages open on the same range as this page.
  const historyTo = (key: MetricKey) => historyPath(key, { from: isoDaysAgo(days), to: isoDaysAgo(0) });

  return (
    <>
      <PageHeader title="Health">
        <p>Sleep, heart rate variability, resting heart rate, recovery, weight and daily activity. Press a tile or a chart title for its history.</p>
      </PageHeader>

      <div className="d-flex justify-content-end mb-3">
        <ButtonGroup aria-label="Date range">
          {RANGES.map(r => (
            <Button key={r} variant={days === r ? 'primary' : 'outline-secondary'} onClick={() => setDays(r)} aria-pressed={days === r}>
              {r} days
            </Button>
          ))}
        </ButtonGroup>
      </div>

      <StatusMessage loading={data.loading} error={data.error} onRetry={data.reload} />

      {rows && (
        <Row className="g-3 mb-3" data-testid="health-latest">
          {latestTiles(rows.latest, historyTo).map(tile => (
            <Col key={tile.label} xs={6} md={3}>
              <StatTile to={tile.to} label={tile.label} value={tile.value} hint={tile.hint} />
            </Col>
          ))}
        </Row>
      )}

      {rows && (
        <Row className="g-3">
          <Col md={6}>
            <ChartCard title="Sleep stages (hours)" historyTo={historyTo('sleep')} empty={empty(rows.sleepRows)}>
              <BarChart data={rows.sleepRows} margin={MARGIN}>
                <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                <YAxis {...Y_AXIS} {...yAxisProps(rows.sleepRows.map(r => ({ total: sum(r.deep, r.rem, r.light, r.awake) })), ['total'], { zero: true })} />
                <Tooltip />
                <Legend />
                <Bar dataKey="deep" name="Deep" stackId="sleep" fill="var(--series-5)" />
                <Bar dataKey="rem" name="REM" stackId="sleep" fill="var(--series-2)" />
                <Bar dataKey="light" name="Light" stackId="sleep" fill="var(--series-7)" />
                <Bar dataKey="awake" name="Awake" stackId="sleep" fill="var(--series-3)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartCard>
          </Col>
          <Col md={6}>
            <ChartCard title="Sleep score" historyTo={historyTo('sleep')} empty={empty(rows.sleepRows)}>
              <LineChart data={rows.sleepRows} margin={MARGIN}>
                <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                <YAxis {...Y_AXIS} {...yAxisProps(undefined, [], { fixed: [0, 100] })} />
                <Tooltip />
                <Line type="monotone" dataKey="score" stroke="var(--chart-1)" dot={false} strokeWidth={2} />
              </LineChart>
            </ChartCard>
          </Col>
          <Col md={6}>
            <ChartCard title="HRV (ms)" historyTo={historyTo('hrv')} empty={empty(rows.hrvRows)}>
              <LineChart data={rows.hrvRows} margin={MARGIN}>
                <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                <YAxis {...Y_AXIS} {...yAxisProps(rows.hrvRows, ['lastNight', 'weekly', 'balancedLow', 'balancedUpper'])} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="lastNight" name="Last night" stroke="var(--chart-1)" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="weekly" name="7-day avg" stroke="var(--chart-3)" dot={false} strokeDasharray="4 4" />
                <Line type="monotone" dataKey="balancedLow" name="Balanced, low" stroke="var(--text-muted)" dot={false} strokeDasharray="2 4" />
                <Line type="monotone" dataKey="balancedUpper" name="Balanced, high" stroke="var(--text-muted)" dot={false} strokeDasharray="2 4" />
              </LineChart>
            </ChartCard>
          </Col>
          <Col md={6}>
            <ChartCard title="Resting heart rate (bpm)" historyTo={historyTo('resting-hr')} empty={empty(rows.restingRows)}>
              <LineChart data={rows.restingRows} margin={MARGIN}>
                <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                <YAxis {...Y_AXIS} {...yAxisProps(rows.restingRows, ['resting'])} />
                <Tooltip />
                <Line type="monotone" dataKey="resting" stroke="var(--chart-4)" dot={false} strokeWidth={2} />
              </LineChart>
            </ChartCard>
          </Col>
          <Col md={6}>
            <ChartCard title="Body battery" historyTo={historyTo('body-battery')} empty={empty(rows.batteryRows)}>
              <AreaChart data={rows.batteryRows} margin={MARGIN}>
                <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                <YAxis {...Y_AXIS} {...yAxisProps(undefined, [], { fixed: [0, 100] })} />
                <Tooltip />
                <Area type="monotone" dataKey="high" stroke="var(--chart-1)" fill="var(--chart-1)" fillOpacity={0.25} />
                <Area type="monotone" dataKey="low" stroke="var(--chart-2)" fill="var(--chart-2)" fillOpacity={0.2} />
              </AreaChart>
            </ChartCard>
          </Col>
          <Col md={6}>
            <ChartCard title="Average stress" historyTo={historyTo('stress')} empty={empty(rows.stressRows)}>
              <LineChart data={rows.stressRows} margin={MARGIN}>
                <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                <YAxis {...Y_AXIS} {...yAxisProps(undefined, [], { fixed: [0, 100] })} />
                <Tooltip />
                <Line type="monotone" dataKey="stress" name="Average" stroke="var(--chart-3)" dot={false} strokeWidth={2} />
              </LineChart>
            </ChartCard>
          </Col>
          <Col xs={12}>
            <ChartCard title="Steps" historyTo={historyTo('steps')} empty={empty(rows.stepRows)}>
              <BarChart data={rows.stepRows} margin={MARGIN}>
                <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                <YAxis {...Y_AXIS} {...yAxisProps(rows.stepRows, ['steps'], { zero: true })} />
                <Tooltip />
                <Bar dataKey="steps" fill="var(--chart-1)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartCard>
          </Col>
          <Col md={6}>
            <ChartCard title="Stress breakdown (% of the day)" historyTo={historyTo('stress')} empty={empty(rows.stressShareRows)}>
              <BarChart data={rows.stressShareRows} margin={MARGIN}>
                <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                <YAxis {...Y_AXIS} {...yAxisProps(undefined, [], { fixed: [0, 100] })} />
                <Tooltip />
                <Legend />
                <Bar dataKey="rest" name="Rest" stackId="stress" fill="var(--series-1)" />
                <Bar dataKey="low" name="Low" stackId="stress" fill="var(--series-2)" />
                <Bar dataKey="medium" name="Medium" stackId="stress" fill="var(--series-3)" />
                <Bar dataKey="high" name="High" stackId="stress" fill="var(--series-6)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartCard>
          </Col>
          <Col md={6}>
            <ChartCard title="Weight (kg)" historyTo={historyTo('weight')} empty={empty(rows.weightRows)}>
              <LineChart data={rows.weightRows} margin={MARGIN}>
                <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                <YAxis {...Y_AXIS} {...yAxisProps(rows.weightRows, ['kg'])} />
                <Tooltip />
                <Line type="monotone" dataKey="kg" name="Weight" stroke="var(--chart-2)" dot={false} strokeWidth={2} connectNulls />
              </LineChart>
            </ChartCard>
          </Col>
          <Col md={6}>
            <ChartCard title="Breathing rate (breaths per minute)" historyTo={historyTo('respiration')} empty={empty(rows.respirationRows)}>
              <LineChart data={rows.respirationRows} margin={MARGIN}>
                <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                <YAxis {...Y_AXIS} {...yAxisProps(rows.respirationRows, ['avg', 'min', 'max'])} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="avg" name="Average" stroke="var(--chart-1)" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="min" name="Lowest" stroke="var(--chart-2)" dot={false} strokeDasharray="4 4" />
                <Line type="monotone" dataKey="max" name="Highest" stroke="var(--chart-4)" dot={false} strokeDasharray="4 4" />
              </LineChart>
            </ChartCard>
          </Col>
          <Col md={6}>
            <ChartCard title="Blood oxygen (%)" historyTo={historyTo('spo2')} empty={empty(rows.spo2Rows)}>
              <LineChart data={rows.spo2Rows} margin={MARGIN}>
                <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                <YAxis {...Y_AXIS} {...yAxisProps(rows.spo2Rows, ['avg', 'min'])} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="avg" name="Average" stroke="var(--chart-1)" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="min" name="Lowest" stroke="var(--chart-4)" dot={false} strokeDasharray="4 4" />
              </LineChart>
            </ChartCard>
          </Col>
          <Col md={6}>
            <ChartCard title="Calories burned (kcal)" historyTo={historyTo('calories')} empty={empty(rows.caloriesRows)}>
              <BarChart data={rows.caloriesRows} margin={MARGIN}>
                <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                <YAxis {...Y_AXIS} {...yAxisProps(rows.caloriesRows.map(r => ({ total: sum(r.active, r.resting) })), ['total'], { zero: true })} />
                <Tooltip />
                <Legend />
                <Bar dataKey="resting" name="Resting" stackId="cal" fill="var(--series-2)" />
                <Bar dataKey="active" name="Active" stackId="cal" fill="var(--series-3)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartCard>
          </Col>
          <Col md={6}>
            <ChartCard title="Intensity minutes" historyTo={historyTo('intensity')} empty={empty(rows.intensityRows)}>
              <BarChart data={rows.intensityRows} margin={MARGIN}>
                <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                <YAxis {...Y_AXIS} {...yAxisProps(rows.intensityRows.map(r => ({ total: sum(r.moderate, r.vigorous) })), ['total'], { zero: true })} />
                <Tooltip />
                <Legend />
                <Bar dataKey="moderate" name="Moderate" stackId="int" fill="var(--series-2)" />
                <Bar dataKey="vigorous" name="Vigorous" stackId="int" fill="var(--series-6)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartCard>
          </Col>
          <Col md={6}>
            <ChartCard title="Floors climbed" historyTo={historyTo('floors')} empty={empty(rows.floorsRows)}>
              <BarChart data={rows.floorsRows} margin={MARGIN}>
                <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                <YAxis {...Y_AXIS} {...yAxisProps(rows.floorsRows, ['floors'], { zero: true })} />
                <Tooltip />
                <Bar dataKey="floors" name="Floors" fill="var(--series-5)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartCard>
          </Col>
          <Col md={6}>
            <ChartCard title="Hydration (ml)" historyTo={historyTo('hydration')} empty={empty(rows.hydrationRows)}>
              <ComposedChart data={rows.hydrationRows} margin={MARGIN}>
                <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                <YAxis {...Y_AXIS} {...yAxisProps(rows.hydrationRows, ['ml', 'goal'], { zero: true })} />
                <Tooltip />
                <Legend />
                <Bar dataKey="ml" name="Drunk" fill="var(--chart-2)" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="goal" name="Goal" stroke="var(--text-muted)" dot={false} strokeDasharray="4 4" />
              </ComposedChart>
            </ChartCard>
          </Col>
        </Row>
      )}
    </>
  );
};

export default HealthPage;
